/**
 * useDataMultiValue Hook
 *
 * Fetches multiple values from a data source for concentric rings,
 * stacked bars, and milestones. Wraps useQueryTemplateData with
 * two extraction modes: "field mode" and "row mode".
 *
 * Created: March 2026 (Phase 5: Data Hooks & Animation)
 */

import { useMemo } from 'react';
import { ChartDataSource } from '@/types/chartDataSource';
import { useQueryTemplateData, isQueryTemplateDataSource } from '@/hooks/useQueryTemplateData';

/**
 * Configuration for a single data field mapping (used in field mode)
 */
export interface FieldConfig {
  /** Field name to extract from data row */
  field: string;
  /** Display label for this value */
  label: string;
  /** Color for rendering this value */
  color: string;
  /** Maximum value (default 100) */
  max?: number;
}

/**
 * Configuration options for useDataMultiValue hook
 */
export interface UseDataMultiValueOptions {
  /** Data source configuration for fetching values */
  dataSource?: ChartDataSource;
  /** Component ID for logging/caching */
  componentId: string;
  /** Extraction mode: 'field' extracts N fields from one row, 'row' iterates rows */
  mode: 'field' | 'row';
  /** Field mode: field configurations for concentric rings or stacked bars */
  fields?: FieldConfig[];
  /** Row mode: field name for milestone name (default: 'name') */
  nameField?: string;
  /** Row mode: field name for milestone completion (default: 'completed') */
  completedField?: string;
  /** Static values fallback when no data source configured (field mode only) */
  staticValues?: Array<{ value: number; label: string; color: string; max?: number }>;
  /** Whether data binding is enabled (default: true) */
  enabled?: boolean;
  /** Direct template execution mode */
  templateId?: string;
}

/**
 * A single value entry extracted in field mode
 */
export interface MultiValueEntry {
  value: number;
  label: string;
  color: string;
  max: number;
}

/**
 * A single milestone entry extracted in row mode
 */
export interface MilestoneEntry {
  id: string;
  name: string;
  completed: boolean;
}

/**
 * Result returned by useDataMultiValue hook
 */
export interface UseDataMultiValueResult {
  /** Extracted values (field mode) — concentric rings or stacked segments */
  values: MultiValueEntry[];
  /** Extracted milestones (row mode) */
  milestones: MilestoneEntry[];
  /** Whether data is currently loading */
  isLoading: boolean;
  /** Error message if data fetch failed */
  error: string | null;
  /** Function to manually refetch data */
  refetch: () => Promise<void>;
  /** Whether a data source is configured and active */
  hasDataSource: boolean;
  /** Timestamp of last successful fetch */
  lastFetched: Date | null;
}

/**
 * Hook for multi-value data binding.
 *
 * Supports two extraction modes:
 * - **Field mode**: Extract N named fields from queryData[0] for concentric rings or stacked bars
 * - **Row mode**: Iterate queryData[] rows for milestone entries
 *
 * Always calls useQueryTemplateData (rules of hooks) — uses `enabled: false` to skip fetching.
 */
export function useDataMultiValue(options: UseDataMultiValueOptions): UseDataMultiValueResult {
  const {
    dataSource,
    componentId,
    mode,
    fields,
    nameField,
    completedField,
    staticValues,
    enabled = true,
    templateId,
  } = options;

  // Always call useQueryTemplateData (rules of hooks)
  const hasDataSource = enabled && (isQueryTemplateDataSource(dataSource) || !!templateId);
  const {
    data: queryData,
    isLoading,
    error,
    refetch,
    lastFetched,
  } = useQueryTemplateData({
    dataSource,
    componentId,
    enabled: hasDataSource,
    templateId,
  });

  // Field mode: extract N named fields from queryData[0]
  const values: MultiValueEntry[] = useMemo(() => {
    if (mode !== 'field') return [];

    // Static fallback when no data source configured
    if (!hasDataSource && staticValues) {
      return staticValues.map(sv => ({
        value: sv.value,
        label: sv.label,
        color: sv.color,
        max: sv.max ?? 100,
      }));
    }

    if (!queryData?.length || !fields?.length) return [];

    return fields.map(f => ({
      value: Number(queryData[0]?.[f.field]) || 0,
      label: f.label,
      color: f.color,
      max: f.max ?? 100,
    }));
  }, [mode, hasDataSource, staticValues, queryData, fields]);

  // Row mode: iterate queryData rows for milestones
  const milestones: MilestoneEntry[] = useMemo(() => {
    if (mode !== 'row') return [];
    if (!queryData?.length) return [];

    return queryData.map((row, i) => ({
      id: String(row.id != null ? row.id : i),
      name: String(row[nameField || 'name'] || `Milestone ${i + 1}`),
      completed: Boolean(row[completedField || 'completed']),
    }));
  }, [mode, queryData, nameField, completedField]);

  return {
    values,
    milestones,
    isLoading: hasDataSource ? isLoading : false,
    error: hasDataSource ? (error || null) : null,
    refetch: refetch || (async () => {}),
    hasDataSource,
    lastFetched: hasDataSource ? lastFetched : null,
  };
}
